<script setup>
import { ref, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import Button from 'dashboard/components-next/button/Button.vue';

// Create-only modal: collects the minimal fields needed to seed a tramite type, then the parent
// routes into the full-page editor where stages / documents / intake are configured.
const emit = defineEmits(['created', 'cancel']);

const { t } = useI18n();
const store = useStore();

const form = ref({
  name: '',
  code: '',
  category: '',
  description: '',
  active: true,
});
const saving = ref(false);

const canSubmit = computed(
  () => form.value.name.trim() && form.value.code.trim()
);

function autoGenerateCode() {
  if (!form.value.code && form.value.name) {
    form.value.code = form.value.name
      .substring(0, 3)
      .toUpperCase()
      .normalize('NFD')
      .replace(/\p{Diacritic}/gu, '');
  }
}

async function handleSubmit() {
  if (!canSubmit.value) return;
  saving.value = true;
  try {
    const type = await store.dispatch('tramiteTypes/createTramiteType', {
      ...form.value,
      code: form.value.code.toUpperCase(),
    });
    useAlert(t('TRAMITE_TYPES.CREATE_SUCCESS'));
    emit('created', type);
  } catch (error) {
    const message =
      error.response?.data?.error || t('TRAMITE_TYPES.SAVE_ERROR');
    useAlert(message);
  } finally {
    saving.value = false;
  }
}
</script>

<template>
  <div class="flex flex-col w-full max-w-lg">
    <woot-modal-header :header-title="t('TRAMITE_TYPES.FORM.TITLE_NEW')" />

    <form class="flex flex-col gap-4 p-6" @submit.prevent="handleSubmit">
      <div class="flex flex-col gap-1">
        <label class="text-sm font-medium text-n-slate-12">
          {{ t('TRAMITE_TYPES.FORM.NAME') }} *
        </label>
        <input
          v-model="form.name"
          type="text"
          required
          maxlength="255"
          :placeholder="t('TRAMITE_TYPES.FORM.NAME_PLACEHOLDER')"
          class="px-3 py-2 w-full rounded-lg border border-n-weak bg-n-alpha-1 focus:border-woot-500 focus:outline-none"
          @blur="autoGenerateCode"
        />
      </div>

      <div class="flex flex-col gap-1">
        <label class="text-sm font-medium text-n-slate-12">
          {{ t('TRAMITE_TYPES.FORM.CODE') }} *
        </label>
        <input
          v-model="form.code"
          type="text"
          required
          maxlength="5"
          pattern="[A-Za-z0-9]+"
          class="px-3 py-2 w-full font-mono uppercase rounded-lg border border-n-weak bg-n-alpha-1 focus:border-woot-500 focus:outline-none"
        />
        <span class="text-xs text-n-slate-10">
          {{ t('TRAMITE_TYPES.FORM.CODE_HELP') }}
        </span>
      </div>

      <div class="flex flex-col gap-1">
        <label class="text-sm font-medium text-n-slate-12">
          {{ t('TRAMITE_TYPES.FORM.CATEGORY') }}
        </label>
        <input
          v-model="form.category"
          type="text"
          maxlength="255"
          :placeholder="t('TRAMITE_TYPES.FORM.CATEGORY_PLACEHOLDER')"
          class="px-3 py-2 w-full rounded-lg border border-n-weak bg-n-alpha-1 focus:border-woot-500 focus:outline-none"
        />
      </div>

      <div class="flex flex-col gap-1">
        <label class="text-sm font-medium text-n-slate-12">
          {{ t('TRAMITE_TYPES.FORM.DESCRIPTION') }}
        </label>
        <textarea
          v-model="form.description"
          rows="2"
          class="px-3 py-2 w-full rounded-lg border border-n-weak bg-n-alpha-1 focus:border-woot-500 focus:outline-none"
        />
      </div>

      <div class="flex gap-2 justify-end pt-4">
        <Button
          type="button"
          slate
          faded
          :label="t('TRAMITE_TYPES.FORM.CANCEL')"
          @click="emit('cancel')"
        />
        <Button
          type="submit"
          :is-loading="saving"
          :disabled="!canSubmit"
          :label="t('TRAMITE_TYPES.FORM.CREATE')"
        />
      </div>
    </form>
  </div>
</template>
